import {t, Trans} from '@lingui/macro'
import {useNavigate} from 'react-router'

import {formatCurrency} from '@product-web/locale/currency'
import {getIsCompanyOwner, getIsFdd, useLoggedInUser} from '@product-web/shell/user'
import type {PromptSize} from '@product-web-features/help-centre/adoption-prompt'
import {AdoptionPrompt} from '@product-web-features/help-centre/adoption-prompt'

import {bffHooks} from './bff-hooks'
import forkliftMoneyImage from './images/forklift-money.png'
import {
    PromptName,
    usePromptsPersistedState,
    useResetPromptsPersistedState,
} from './overdraft-adoption-prompts-state'

const useAdoptionPrompts = () => {
    const user = useLoggedInUser()
    const isOwner = getIsCompanyOwner(user)
    const isFdd = getIsFdd(user)

    return bffHooks.credit.getOverdraftAdoptionPrompts.useQuery(undefined, {
        enabled: isOwner && isFdd,
        staleTime: Infinity,
    })
}

export const OverdraftAdoptionPrompts = () => {
    const user = useLoggedInUser()
    const isOwner = getIsCompanyOwner(user)
    const {data} = useAdoptionPrompts()

    const {canBeActivated, hasDeclinedTransactions, hasLowBalance, terms} = data ?? {}

    const creditLimit = formatCurrency(terms?.limit, terms?.currency, {
        format: {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        },
    })

    useResetPromptsPersistedState()
    const {dismissPrompt, dismissedPrompts} = usePromptsPersistedState()
    const onDismiss = () => {
        dismissPrompt()
    }

    const navigate = useNavigate()
    const isVisible = isOwner && !dismissedPrompts?.dismissedAt

    const onComplete = () => {
        dismissPrompt()
        navigate('/wallet/overdraft/activate')
    }

    const commonProps = {
        size: 'medium' as PromptSize,
        completeText: t`Start activation`,
        dismissText: t`Not now`,
        imageSrc: forkliftMoneyImage,
        onDismiss,
        onComplete,
    }

    if (!isVisible) {
        return null
    }

    if (hasDeclinedTransactions) {
        if (canBeActivated) {
            return (
                <AdoptionPrompt
                    {...commonProps}
                    name={`${PromptName.DECLINED_TRANSACTIONS}-overdraft`}
                    title={
                        <Trans>Declined transactions? Not with Overdraft of {creditLimit}</Trans>
                    }
                    text={
                        <Trans>
                            Activate Overdraft limit of up to {creditLimit} and enjoy it
                            interest-free with a 30-day repayment.
                        </Trans>
                    }
                />
            )
        }
    }
    if (hasLowBalance) {
        if (canBeActivated) {
            return (
                <AdoptionPrompt
                    {...commonProps}
                    name={`${PromptName.LOW_BALANCE}-overdraft`}
                    title={<Trans>Low Wallet balance? Don't worry about it with Overdraft</Trans>}
                    text={
                        <Trans>
                            Activate Overdraft limit of up to {creditLimit} and enjoy it
                            interest-free with a 30-day repayment.
                        </Trans>
                    }
                />
            )
        }
    }
    return null
}
